/**
 * services/reconciliation/reconciliation.service.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Cash counts and what their differences cost someone.
 *
 * Two rules are enforced here before the request goes out:
 *   - Waiving requires a reason (invariant 3 — no variance is absorbed).
 *   - Every screen that lists counts is gated on variance routing being set,
 *     which the API reports as 412 + `details.gate: 'variance_set'`. Callers
 *     read that with utils/errorHandler `gateOf` and render a gate bar rather
 *     than an error.
 * ──────────────────────────────────────────────────────────────────────────
 */
import axiosInstance from '../../api/axiosInstance';
import {
  Cash_Counts_Url,
  Cash_Reconciliation_Resolve_Url,
  Cash_Reconciliations_Url,
} from '../../api/endpoints';
import { parseResponse } from '../../schemas/common';
import {
  reconciliationsResponseSchema,
  type CountCreate,
  type Reconciliation,
  type ReconciliationResponse,
} from '../../schemas/reconciliation';

/** The counts and the gate map — the gate rides in the body with a 200. */
export async function fetchReconciliations(status?: string): Promise<ReconciliationResponse> {
  const { data } = await axiosInstance.get(Cash_Reconciliations_Url, { params: { status } });
  return parseResponse(reconciliationsResponseSchema, data, 'GET /v1/reconciliation');
}

/**
 * Record a count. The EXPECTED figure is never sent — the API takes it from the
 * balance projection or the assignment's limit, because an operator who could
 * choose both sides of a reconciliation is not reconciling anything.
 */
export async function recordCount(input: CountCreate): Promise<unknown> {
  const { data } = await axiosInstance.post(Cash_Counts_Url, input);
  return data;
}

/**
 * Resolve a variance: `route` posts it to Payroll or Billing per the org's
 * routing, `waive` writes it off with a reason. There is no third option, and
 * that is the whole of invariant 3.
 */
export async function resolveVariance(
  id: string,
  action: 'route' | 'waive',
  reason?: string,
): Promise<unknown> {
  if (action === 'waive' && !reason?.trim()) {
    throw new Error('Waiving a variance requires a reason.');
  }
  const { data } = await axiosInstance.post(Cash_Reconciliation_Resolve_Url(id), {
    action,
    ...(reason?.trim() ? { reason: reason.trim() } : {}),
  });
  return data;
}

export type { Reconciliation };
